import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import HeroCard from "../components/HeroCard";
import NewsCard from "../components/NewsCard";
import VideoCard from "../components/VideoCard";
import PodCard from "../components/PodCard";
import AdSlot from "../components/AdSlot";
import NearYouStrip from "../components/NearYouStrip";
import NewsezeDifference from "../components/NewsezeDifference";
import SocialNetworksStrip from "../components/SocialNetworksStrip";
import BriefModal from "../components/BriefModal";
import ShareModal from "../components/ShareModal";
import DailyBriefModal from "../components/DailyBriefModal";
import SponsorModal from "../components/SponsorModal";
import InternationalSection from "../components/InternationalSection";
import CreditsWidget from "../components/CreditsWidget";
import BuildezeSection from "../components/BuildezeSection";
import PvStatsStrip from "../components/PvStatsStrip";
import MoneyMakerTile from "../components/MoneyMakerTile";
import XConversation from "../components/XConversation";
import JsonLd from "../components/JsonLd";
import PersonalizationModal, { loadPrefs } from "../components/PersonalizationModal";
import { MagazineCard } from "./Newsstand";
import DivergentWidget from "../components/DivergentWidget";
import { fetchFeatured, fetchNews, fetchVideos, fetchPodcasts } from "../lib/api";
import useNewsezeUser from "../lib/useNewsezeUser";
import axios from "axios";
import { Mail, Sparkles, Video, Headphones, ArrowRight, Coins, Gift, Shield, ShoppingCart, Plane, Cpu, Briefcase, HeartPulse, Newspaper, Globe, Landmark, MapPin, Trophy, Music, BookOpen, ExternalLink } from "lucide-react";

const NEWSSTAND_API = `${process.env.REACT_APP_BACKEND_URL}/api/newsstand/featured`;
const API = `${process.env.REACT_APP_BACKEND_URL}/api/news`;

// Keyword-derived category filters (no backend retag needed).
const CRIME_KW = /\b(police|shoot|shooting|arrest|murder|robber|kidnap|fentanyl|border patrol|cartel|gang|indictment|sentenc|sheriff|fbi|doj|dea|ice agent|crime|crimin|stab|assault|trafficking|fraud|homicide|fugitive|raid|smuggl|prosecut|gunfire|hijack|hostage|terror)\b/i;
const CONSUMER_KW = /\b(inflation|housing market|mortgage|rent\b|gas price|grocer|wage|jobs report|unemployment|tax cut|tax hike|retirement|social security|medicare|healthcare cost|insurance premium|interest rate|fed rate|grocer|cost of living|paycheck|cpi|ppi|consumer price|federal reserve)\b/i;
const LIFESTYLE_KW = /\b(travel|vacation|destination|cruise|recipe|cuisine|restaurant|wine|cocktail|wellness|fitness|workout|parent|family|garden|interior|fashion|beauty|hike|hiking|cabin|camping|hobby|lifestyle|home decor)\b/i;
const TECH_AI_KW = /\b(ai|artificial intelligence|chatgpt|openai|anthropic|gpt|llm|machine learning|robot|chip|nvidia|cloud comput|cyber|software|app|startup|tech stock)\b/i;
const HEALTH_KW = /\b(health|disease|cancer|virus|vaccine|hospital|fda|cdc|outbreak|drug approv|clinical trial|medicine|surgery|patient|covid|alzheimer|diabetes|obesity|mental health|nutrition|epidemic|pandemic)\b/i;

// Collector / hobbyist interest keywords — drive the new "Interests" section.
// These align with passion categories that drive long reading sessions and
// repeat visits (research finding: hobbyists spend 3-4x longer per session
// than general news readers).
const INTEREST_KW = {
  classic_cars: { label: "Classic Cars", icon: "🚗", re: /\b(classic car|muscle car|vintage car|barrett-jackson|mecum|monterey car week|pebble beach|concours|hot rod|corvette|mustang|camaro|porsche 911|ferrari|restomod|restoration|cars and coffee)\b/i },
  watches: { label: "Watches", icon: "⌚", re: /\b(rolex|omega|patek philippe|audemars piguet|tudor|cartier watch|tag heuer|breitling|grand seiko|watchmaking|horology|wristwatch|chronograph|GMT-master|submariner|swiss watch)\b/i },
  precious_metals: { label: "Precious Metals", icon: "🥇", re: /\b(gold price|silver price|gold bullion|precious metal|gold etf|spot gold|spot silver|comex|gold reserve|gold mine|platinum)\b/i },
  sports_cards: { label: "Sports Cards", icon: "🃏", re: /\b(sports card|trading card|panini|topps|baseball card|psa graded|rookie card|card show|sports collectibles|memorabilia auction)\b/i },
  guitars: { label: "Guitars", icon: "🎸", re: /\b(fender|gibson|stratocaster|telecaster|les paul|martin guitar|taylor guitar|guitar collector|vintage guitar|reverb\.com|guitar amp|amplifier)\b/i },
  real_estate: { label: "Real Estate", icon: "🏠", re: /\b(real estate|home price|housing market|mortgage rate|zillow|realtor|home sale|property tax|rental market|home buyer|first-time buyer|home equity|hoa|landlord)\b/i },
  aviation: { label: "Aviation", icon: "✈️", re: /\b(boeing|airbus|airline|pilot|aviation|aircraft|jet engine|fighter jet|cessna|gulfstream|business jet|faa|ntsb|aviation safety|spirit aero|delta air|united air|american air|southwest air)\b/i },
};

// Section header — same shape for every category
// Clean, color-accented section header — same shape for every category so
// the home page reads as ONE polished news product.
function SectionHead({ color, icon, title, subtitle, href }) {
  return (
    <div className="flex items-end justify-between mb-4 border-b border-[var(--line)] pb-2">
      <div className="flex items-center gap-3">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-md text-white shrink-0" style={{ backgroundColor: color }}>{icon}</span>
        <div>
          <h2 className="text-[19px] md:text-[22px] font-black tracking-[-0.025em] text-[var(--ink)] leading-none">{title}</h2>
          {subtitle && <div className="text-[11.5px] text-[var(--muted)] mt-1 leading-tight">{subtitle}</div>}
        </div>
      </div>
      {href && (
        <Link to={href} className="text-[11px] uppercase tracking-[0.18em] font-black text-[var(--blue)] hover:text-[var(--ink)] shrink-0 hidden sm:block">
          All →
        </Link>
      )}
    </div>
  );
}

const SECTION_LABEL = {
  top: "National Headlines",
  local: "Local",
  world: "World",
  government: "Politics",
  economy: "Business",
  markets: "Markets",
  sports: "Sports",
  technology: "Tech",
  health: "Health",
  medicine: "Medicine",
  science: "Science",
  space: "Space",
  realestate: "Real Estate",
  entertainment: "Entertainment",
  hollywood: "Hollywood",
};

export default function Home() {
  const [feat, setFeat] = useState(null);
  const [videos, setVideos] = useState([]);
  const [pods, setPods] = useState([]);
  const [voicePods, setVoicePods] = useState([]);   // free center & conservative shows
  const [aroundNation, setAroundNation] = useState([]);
  const [newsstand, setNewsstand] = useState([]);   // magazine featured strip
  const [prefs, setPrefs] = useState(loadPrefs);
  const [prefsOpen, setPrefsOpen] = useState(false);
  // Listen for live pref changes (modal save) so the home re-renders instantly
  useEffect(() => {
    const onChange = (e) => setPrefs(e.detail || loadPrefs());
    window.addEventListener("newseze:prefs-changed", onChange);
    return () => window.removeEventListener("newseze:prefs-changed", onChange);
  }, []);
  const [latest, setLatest] = useState([]);
  const [allRecent, setAllRecent] = useState([]); // broader pool for derived categories
  const [brief, setBrief] = useState(null);
  const [share, setShare] = useState(null);
  const [showBriefSub, setShowBriefSub] = useState(false);
  const [showSponsor, setShowSponsor] = useState(false);
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref") || "";
  const { track } = useNewsezeUser();

  // Auto-open the Daily Brief modal when arriving with a ?ref=CODE link
  useEffect(() => {
    if (ref) setShowBriefSub(true);
  }, [ref]);

  useEffect(() => {
    let alive = true;
    const applyResults = (f, v, p, voices, l, all, mags, atn) => {
      if (!alive) return;
      setFeat(f);
      setVideos(v);
      setPods(p);
      setVoicePods(voices);
      setLatest(l);
      setAllRecent(all);
      setNewsstand(mags);
      setAroundNation(atn);
    };
    const load = async () => {
      try {
        const [f, v, p, voices, l, all, mags, atn] = await Promise.all([
          fetchFeatured(),
          fetchVideos({ limit: 6 }),
          fetchPodcasts({ network: "Investigations" }),
          fetchPodcasts({ network: "Voices", free_only: true, limit: 12 }),
          fetchNews({ limit: 20 }),
          fetchNews({ limit: 100 }), // big pool for keyword-derived sections
          axios.get(NEWSSTAND_API).then((r) => r.data?.items || []).catch(() => []),
          axios.get(`${API}/around-nation`).then((r) => r.data || []).catch(() => []),
        ]);
        applyResults(f, v, p, voices, l, all, mags, atn);
      } catch (e) { console.error(e); }
    };
    load();
    // refresh every 5 minutes so breaking news surfaces without manual reload
    const id = setInterval(load, 5 * 60 * 1000);
    // Also reload at the top of every hour so hero changes instantly
    const now = new Date();
    const msToNextHour = 3600000 - (now.getMinutes() * 60000 + now.getSeconds() * 1000);
    const hourId = setTimeout(load, msToNextHour + 1000);
    return () => { alive = false; clearInterval(id); clearTimeout(hourId); };
  }, []);

  const sections = feat?.sections || {};
  const ticker = (latest || []).slice(0, 6);
  // Photo-only filter — every section grid hides imageless articles so we never
  // ship a wall of gradient placeholders.
  const hasPhoto = (a) => !!(a && a.image);
  const withPhotos = (arr) => (arr || []).filter(hasPhoto);
  const sec = (k) => withPhotos(sections[k]);
  // Keyword-derived filters across the broader recent pool, dedup by id.
  const matchKW = (re) => (a) => a && (re.test(a.title || "") || re.test(a.summary || ""));
  const deriveByKW = (re, maxN = 12) => {
    const pool = [...(allRecent || []), ...(latest || [])];
    const seen = new Set();
    const out = [];
    for (const a of pool) {
      if (!hasPhoto(a) || !matchKW(re)(a) || seen.has(a.id)) continue;
      seen.add(a.id);
      out.push(a);
      if (out.length >= maxN) break;
    }
    return out;
  };

  return (
    <>
      <JsonLd
        id="newseze-jsonld-org"
        data={{
          "@context": "https://schema.org",
          "@type": "NewsMediaOrganization",
          name: "Newseze",
          url: "https://newseze.com/",
          logo: {
            "@type": "ImageObject",
            url: "https://newseze.com/logo.png",
          },
          sameAs: [
            "https://twitter.com/newseze",
            "https://www.facebook.com/newseze",
          ],
          description:
            "Newseze is a balanced AI-curated news aggregator that rewards readers. No drama. No conspiracies.",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://newseze.com/search?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }}
      />
      <Header ticker={ticker} onOpenBrief={() => setShowBriefSub(true)} />

      <main className="max-w-[1280px] mx-auto px-5">
        {/* Tiny one-line trust strip — public-facing positioning is "center to
            slight right". The 70/20/10 content target stays internal newsroom
            guidance only (not shown to readers). */}
        <section className="pt-4 pb-2" data-testid="trust-strip">
          <div className="flex items-center justify-between gap-3 text-[11px] uppercase tracking-[0.16em] font-black border-b border-[var(--line)] pb-2">
            <div className="flex items-center gap-2 text-[var(--muted)]">
              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[var(--gold)] text-white text-[8px]">✓</span>
              <span className="text-[var(--ink)]">No drama · No conspiracies</span>
              <span className="hidden sm:inline text-[var(--muted)]">·</span>
              <span className="hidden sm:inline text-[var(--muted)]">Trust-ranked · Facts first · You decide</span>
            </div>
            <div className="flex items-center gap-3 text-[var(--muted)]">
              <button onClick={() => setPrefsOpen(true)} className="hover:text-[var(--blue)] inline-flex items-center gap-1 font-black" data-testid="trust-strip-personalize">⚙ Personalize</button>
              <span className="hidden sm:inline">·</span>
              <Link to="/trust" className="hover:text-[var(--blue)]" data-testid="trust-strip-trust">Editorial trust</Link>
              <span className="hidden sm:inline">·</span>
              <Link to="/rewards" className="hover:text-[var(--blue)]" data-testid="trust-strip-rewards">Rewards</Link>
              <span className="hidden sm:inline">·</span>
              <Link to="/newsroom" className="hover:text-[var(--blue)]" data-testid="trust-strip-newsroom">Newsroom</Link>
            </div>
          </div>
        </section>

        {/* Near You — geo-detected local headlines (TOP placement per editorial brief) */}
        <NearYouStrip onBrief={setBrief} onShare={setShare} />

        {/* The Newseze Difference — value-prop band introducing all 6 signature
            features (calm filter, trust, custom, investigator, ask-the-reporter,
            rewards) plus the "Pick any topic / story / station" interactive panel. */}
        <NewsezeDifference />

        {/* Social networks · score any post · 11 supported networks (Truth Social
            in top 5 per editorial directive 2026-06-16). */}
        <SocialNetworksStrip />

        {/* Single hero + secondaries below — NEWS LEADS THE PAGE */}
        <section className="pb-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          <div className="space-y-5">
            {feat?.hero ? (
              <HeroCard article={feat.hero} onBrief={setBrief} onShare={setShare} />
            ) : (
              /* Skeleton placeholder so the hero column never collapses while
                 /api/news/featured is loading (root cause of mobile users seeing
                 promo widgets first instead of news). */
              <div className="rounded-md border border-[var(--line)] overflow-hidden bg-white animate-pulse" data-testid="hero-skeleton">
                <div className="aspect-[16/9] bg-[var(--sky)]" />
                <div className="p-5 space-y-3">
                  <div className="h-3 w-24 bg-[var(--sky)] rounded" />
                  <div className="h-8 w-11/12 bg-[var(--sky)] rounded" />
                  <div className="h-8 w-3/4 bg-[var(--sky)] rounded" />
                  <div className="h-4 w-5/6 bg-[var(--sky)] rounded" />
                  <div className="h-4 w-4/6 bg-[var(--sky)] rounded" />
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {feat?.secondaries?.length ? (
                withPhotos(feat.secondaries).slice(0, 4).map((a) => (
                  <NewsCard key={a.id} article={a} onBrief={setBrief} onShare={setShare} />
                ))
              ) : (
                /* 4 secondary skeletons so the grid below the hero also keeps shape */
                [0, 1, 2, 3].map((i) => (
                  <div key={i} className="rounded-md border border-[var(--line)] bg-white overflow-hidden animate-pulse" data-testid={`secondary-skeleton-${i}`}>
                    <div className="aspect-[16/10] bg-[var(--sky)]" />
                    <div className="p-3 space-y-2">
                      <div className="h-3 w-16 bg-[var(--sky)] rounded" />
                      <div className="h-5 w-11/12 bg-[var(--sky)] rounded" />
                      <div className="h-5 w-3/4 bg-[var(--sky)] rounded" />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right rail — on mobile (single column) these stack BELOW the hero
              + secondaries. The hero skeleton above keeps the news column tall
              so right-rail widgets never collapse above the fold. */}
          <aside className="space-y-5">
            <CreditsWidget onOpenBrief={() => setShowBriefSub(true)} />

            <DivergentWidget />

            <AdSlot size="h-rect" slot="homepage-top" />

            <div className="bg-white rounded-md border border-[var(--line)] p-4">
              <div className="text-[11px] uppercase tracking-[0.14em] font-black text-[var(--blue)] mb-2">
                Latest
              </div>
              <ul className="space-y-3">
                {latest.slice(0, 6).map((a) => (
                  <li key={a.id} className="border-b border-[var(--line)] pb-3 last:border-0 last:pb-0" data-testid={`latest-${a.id}`}>
                    <Link to={`/article/${a.id}`} className="block text-[14px] font-bold leading-snug text-[var(--ink)] hover:text-[var(--blue)]">
                      {a.title}
                    </Link>
                    <div className="flex items-center gap-2 text-[11px] text-[var(--muted)] mt-1 flex-wrap">
                      <span>{a.source}</span>
                      {typeof a.trust === "number" && a.trust > 0 && (
                        <span className="text-[var(--blue)] font-black uppercase tracking-[0.12em]">trust {a.trust}</span>
                      )}
                      <Link to={`/article/${a.id}#ask-us-section`} className="ml-auto text-[var(--blue)] font-black underline">Ask</Link>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="sponsor-cta" data-testid="rail-sponsor-cta">
              <div className="text-[11px] uppercase tracking-[0.18em] font-black text-[var(--gold)]">Sponsor Newseze</div>
              <h4 className="text-[18px] font-black mt-1 leading-tight">Reach calm, high-intent readers.</h4>
              <p className="text-[12.5px] opacity-85 mt-1.5">Contextual placement beside trust-scored stories. No bot traffic.</p>
              <button onClick={() => setShowSponsor(true)} className="btn gold mt-3 w-full justify-center">Request media kit</button>
            </div>

            {/* Live X conversation — read-only public posts from mainstream wire/news accounts */}
            <XConversation handle="Reuters" />
          </aside>
        </section>

        {/* ============================= ROW 1: FOR YOU — personalized from prefs ============================= */}
        {(() => {
          // Pull articles matching the reader's favorite categories.
          // No perspective/lean filtering — readers form their own interpretation.
          // The slight-right editorial dial runs silently server-side via _signal_score.
          const cats = prefs.categories || [];
          const catMap = { local: "local", national: "top", politics: "government", business: "economy", sports: "sports", technology: "technology", entertainment: "entertainment", world: "world", lifestyle: "culture" };
          const pool = [];
          const seen = new Set();
          // 1) Pick from explicitly favorite categories first
          for (const c of cats) {
            const bk = catMap[c] || c;
            for (const a of withPhotos((feat?.sections || {})[bk] || [])) {
              if (seen.has(a.id)) continue;
              seen.add(a.id); pool.push(a);
              if (pool.length >= 6) break;
            }
            if (pool.length >= 6) break;
          }
          // 2) Backfill with recent articles
          if (pool.length < 6) {
            for (const a of withPhotos(allRecent.length ? allRecent : latest)) {
              if (seen.has(a.id)) continue;
              seen.add(a.id); pool.push(a);
              if (pool.length >= 6) break;
            }
          }
          if (pool.length === 0) return null;
          return (
            <section className="py-6" data-testid="section-foryou">
              <SectionHead color="#b8860b" icon={<Sparkles size={16} />} title="For You" subtitle={`Personalized to your interests${cats.length ? ` · ${cats.length} favorite categor${cats.length === 1 ? 'y' : 'ies'}` : ""}`} href="/#buildeze" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {pool.slice(0, 6).map((a) => (<NewsCard key={a.id} article={a} onBrief={setBrief} onShare={setShare} />))}
              </div>
              <div className="mt-3 text-[11px] text-[var(--muted)]">
                <button onClick={() => setPrefsOpen(true)} className="text-[var(--blue)] hover:text-[var(--ink)] font-bold inline-flex items-center gap-1" data-testid="foryou-personalize">⚙ Refine your preferences</button>
                <span> · Saved on this device. Sign in to sync.</span>
              </div>
            </section>
          );
        })()}

        {/* ============================= SIX MAIN SECTIONS — per the spec:
            each = one large image + one featured article + three smaller headlines.
            Rendered as 2 rows of 3 cards (Local · National · World / Politics · Business · Sports).
            These REPLACE the old big 6-card grids for these categories. ============================= */}
        {feat && (
          <section className="py-8" data-testid="section-six-main">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {[
                { key: "local", label: "Local", icon: <MapPin size={14} />, color: "#0e7490", path: "/section/local", pool: sec("local") },
                { key: "national", label: "National", icon: <Newspaper size={14} />, color: "#1e3a8a", path: "/news", pool: sec("top").length ? sec("top") : withPhotos(latest).filter((a) => !a.is_international) },
                { key: "world", label: "World", icon: <Globe size={14} />, color: "#1e40af", path: "/section/world", pool: [...withPhotos(feat?.international_top), ...sec("world")] },
                { key: "politics", label: "Politics", icon: <Landmark size={14} />, color: "#7c2d12", path: "/section/government", pool: sec("government") },
                { key: "business", label: "Business & Markets", icon: <Briefcase size={14} />, color: "#0f766e", path: "/section/markets", pool: [...sec("markets"), ...sec("economy")] },
                { key: "sports", label: "Sports", icon: <Trophy size={14} />, color: "#ea580c", path: "/section/sports", pool: sec("sports") },
              ].map((cat) => {
                const lead = cat.pool[0];
                const sub = cat.pool.slice(1, 4);
                if (!lead) return null;
                return (
                  <div key={cat.key} className="bg-white rounded-lg border border-[var(--line)] overflow-hidden flex flex-col" data-testid={`six-main-${cat.key}`}>
                    <Link to={`/article/${lead.id}`} className="block aspect-[16/10] bg-cover bg-center" style={{ backgroundImage: `url(${lead.image})` }} />
                    <div className="px-4 py-3 border-b border-[var(--line)] flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-md text-white" style={{ backgroundColor: cat.color }}>{cat.icon}</span>
                      <h3 className="text-[15px] font-black tracking-[-0.02em] text-[var(--ink)]">{cat.label}</h3>
                      <Link to={cat.path} className="ml-auto text-[10px] uppercase tracking-[0.18em] font-black text-[var(--blue)] hover:text-[var(--ink)]">All →</Link>
                    </div>
                    <Link to={`/article/${lead.id}`} className="px-4 py-3 hover:bg-[#fff9ed] block">
                      <div className="text-[10px] uppercase tracking-[0.16em] font-black text-[var(--gold)] mb-1">Newseze Wire · {cat.label}</div>
                      <div className="text-[15.5px] font-black leading-snug text-[var(--ink)] line-clamp-2 mb-1.5">{lead.title}</div>
                      {(lead.summary || lead.why_it_matters) && (
                        <div className="text-[12px] text-[var(--muted)] leading-snug line-clamp-2">{(lead.summary || lead.why_it_matters || "").slice(0, 180)}</div>
                      )}
                      {lead.source && (
                        <div className="text-[9.5px] text-[var(--muted)] mt-1.5 uppercase tracking-[0.12em] font-bold opacity-70">Wire: {lead.source}</div>
                      )}
                    </Link>
                    {sub.length > 0 && (
                      <ul className="border-t border-[var(--line)] mt-auto">
                        {sub.map((a) => (
                          <li key={a.id}>
                            <Link to={`/article/${a.id}`} className="flex gap-2 px-4 py-2.5 hover:bg-[#fff9ed] transition border-b border-[var(--line)] last:border-b-0">
                              <span className="text-[var(--gold)] mt-0.5 shrink-0">•</span>
                              <span className="text-[12.5px] leading-snug text-[var(--ink)] line-clamp-2 font-bold">{a.title}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ============================= ROW 3 (3-up: Tech · Entertainment · Health) — premium lead+thumbs treatment for the next-tier subjects so Six Main isn't carrying everything visually ============================= */}
        {(sec("technology").length + sec("entertainment").length + sec("hollywood").length + sec("health").length + sec("medicine").length) > 0 && (
          <section className="py-2" data-testid="section-row3">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {[
                { key: "tech", label: "Technology & AI", icon: <Cpu size={14} />, color: "#7c3aed", path: "/section/technology", pool: sec("technology") },
                { key: "entertainment", label: "Entertainment", icon: <Music size={14} />, color: "#a21caf", path: "/section/entertainment", pool: [...sec("entertainment"), ...sec("hollywood")] },
                { key: "health", label: "Health & Medical", icon: <HeartPulse size={14} />, color: "#dc2626", path: "/section/health", pool: [...sec("health"), ...sec("medicine")] },
              ].map((cat) => {
                const lead = cat.pool[0];
                const sub = cat.pool.slice(1, 4);
                if (!lead) return null;
                return (
                  <div key={cat.key} className="bg-white rounded-lg border border-[var(--line)] overflow-hidden flex flex-col" data-testid={`row3-${cat.key}`}>
                    <Link to={cat.path} className="block aspect-[16/10] bg-cover bg-center" style={{ backgroundImage: `url(${lead.image})` }} />
                    <div className="px-4 py-3 border-b border-[var(--line)] flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-md text-white" style={{ backgroundColor: cat.color }}>{cat.icon}</span>
                      <h3 className="text-[15px] font-black tracking-[-0.02em] text-[var(--ink)]">{cat.label}</h3>
                      <Link to={cat.path} className="ml-auto text-[10px] uppercase tracking-[0.18em] font-black text-[var(--blue)] hover:text-[var(--ink)]">More →</Link>
                    </div>
                    <Link to={`/article/${lead.id}`} className="px-4 py-3 hover:bg-[#fff9ed] block">
                      <div className="text-[10px] uppercase tracking-[0.16em] font-black text-[var(--gold)] mb-1">Newseze Wire · {cat.label}</div>
                      <div className="text-[15px] font-black leading-snug text-[var(--ink)] line-clamp-2">{lead.title}</div>
                      {lead.source && (
                        <div className="text-[9.5px] text-[var(--muted)] mt-1.5 uppercase tracking-[0.12em] font-bold opacity-70">Wire: {lead.source}</div>
                      )}
                    </Link>
                    {sub.length > 0 && (
                      <ul className="border-t border-[var(--line)]">
                        {sub.map((a) => (
                          <li key={a.id}>
                            <Link to={`/article/${a.id}`} className="flex gap-2 px-4 py-2.5 hover:bg-[#fff9ed] transition border-b border-[var(--line)] last:border-b-0">
                              <span className="text-[var(--gold)] mt-0.5 shrink-0">•</span>
                              <span className="text-[12.5px] leading-snug text-[var(--ink)] line-clamp-2 font-bold">{a.title}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ============================= ROW 4: COMMUNITY & LOCAL HUB ============================= */}
        <section className="py-8" data-testid="section-community-hub">
          <SectionHead color="#0e7490" icon={<MapPin size={14} />} title="Community & Local Hub" subtitle="ZIP-based · the news that affects you where you live" href="/section/local" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {(() => {
              const crime = deriveByKW(CRIME_KW, 4);
              return [
                { key: "crime", label: "Crime & Safety", icon: <Shield size={18} />, color: "#b91c1c", items: crime, fallback: "Police, sheriff, courts — local public-safety stories surface here as they break." },
                { key: "weather", label: "Weather", icon: <span>☁️</span>, color: "#0891b2", items: [], fallback: "Severe-weather alerts, forecast briefs, and NWS warnings near your ZIP. Plug-in coming soon." },
                { key: "traffic", label: "Traffic", icon: <span>🚗</span>, color: "#d97706", items: [], fallback: "Major commute closures + transit advisories. Plug-in coming soon." },
                { key: "events", label: "Events", icon: <span>🎟</span>, color: "#7c3aed", items: [], fallback: "Concerts, fairs, sports, town halls — community-submitted via Buildeze." },
                { key: "government", label: "Government", icon: <Landmark size={18} />, color: "#1e3a8a", items: sec("government").slice(0, 4), fallback: "City council, school board, county supervisors — local decisions you'd otherwise miss." },
              ].map((tile) => (
                <div key={tile.key} className="bg-white border border-[var(--line)] rounded-lg overflow-hidden flex flex-col" data-testid={`community-tile-${tile.key}`}>
                  <div className="px-3 py-2.5 border-b border-[var(--line)] flex items-center gap-2" style={{ backgroundColor: `${tile.color}0F` }}>
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-md text-white shrink-0" style={{ backgroundColor: tile.color }}>{tile.icon}</span>
                    <h3 className="text-[12.5px] font-black uppercase tracking-[0.06em] text-[var(--ink)]">{tile.label}</h3>
                  </div>
                  {tile.items.length > 0 ? (
                    <ul className="flex-1 divide-y divide-[var(--line)]">
                      {tile.items.slice(0, 3).map((a) => (
                        <li key={a.id}>
                          <Link to={`/article/${a.id}`} className="block px-3 py-2.5 hover:bg-[#fff9ed]">
                            <div className="text-[12.5px] font-black text-[var(--ink)] leading-snug line-clamp-2">{a.title}</div>
                            <div className="text-[10px] text-[var(--muted)] mt-0.5">{a.source}</div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="p-3 flex-1 flex flex-col items-start justify-between">
                      <p className="text-[11.5px] text-[var(--muted)] leading-snug">{tile.fallback}</p>
                      <span className="text-[10px] uppercase tracking-[0.14em] font-black text-[var(--gold)] mt-2">Coming soon</span>
                    </div>
                  )}
                </div>
              ));
            })()}
          </div>
        </section>

        {/* Banner ad */}
        <AdSlot size="banner" slot="homepage-mid-banner" />

        {/* ============================= 1. TOP STORIES (12 — most important across all subjects) ============================= */}
        {feat && (() => {
          const seen = new Set();
          const pool = [];
          const push = (a) => { if (a && hasPhoto(a) && !seen.has(a.id)) { seen.add(a.id); pool.push(a); } };
          push(feat.hero);
          (feat.secondaries || []).forEach(push);
          // One top story from every available section so coverage stays wide
          Object.values(feat.sections || {}).forEach((items) => withPhotos(items).slice(0, 1).forEach(push));
          // Backfill with seconds across priority sections
          ["top", "government", "world", "economy", "markets", "local"].forEach((cat) => {
            withPhotos(feat.sections && feat.sections[cat]).slice(1, 3).forEach(push);
          });
          if (pool.length === 0) return null;
          return (
            <section className="py-8" data-testid="section-top-stories">
              <SectionHead color="var(--gold)" icon={<Newspaper size={16} />} title="Top Stories" subtitle="The biggest news today — ranked across all six subjects" href="/section/top" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {pool.slice(0, 12).map((a) => (<NewsCard key={a.id} article={a} onBrief={setBrief} onShare={setShare} />))}
              </div>
            </section>
          );
        })()}

        {/* ============================= 1b. MOST TRUSTED TODAY — emphasizes editorial trust system ============================= */}
        {feat && (() => {
          const seen = new Set();
          const all = [];
          const collect = (arr) => (arr || []).forEach((a) => { if (a && hasPhoto(a) && !seen.has(a.id) && typeof a.trust === "number") { seen.add(a.id); all.push(a); } });
          collect(feat.secondaries);
          Object.values(feat.sections || {}).forEach(collect);
          const trusted = all.sort((a, b) => (b.trust || 0) - (a.trust || 0)).slice(0, 5);
          if (trusted.length < 3) return null;
          const [lead, ...rest] = trusted;
          return (
            <section className="py-8" data-testid="section-most-trusted">
              <SectionHead color="#0a1f3d" icon={<Shield size={16} />} title="Most Trusted Today" subtitle="Highest editorial trust scores — verified newsrooms · evidence-ranked · respect-checked" href="/trust" />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Lead trusted story — large card */}
                <Link to={`/article/${lead.id}`} className="group bg-white rounded-lg border border-[var(--line)] overflow-hidden flex flex-col hover:border-[var(--gold)] hover:shadow-md transition" data-testid={`most-trusted-lead-${lead.id}`}>
                  <div className="aspect-[16/9] bg-cover bg-center relative" style={{ backgroundImage: `url(${lead.image})` }}>
                    <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-[#0a1f3d] text-white px-2.5 py-1 rounded-md shadow-lg">
                      <Shield size={12} className="text-[var(--gold)]" />
                      <span className="text-[10px] uppercase tracking-[0.16em] font-black">Trust {lead.trust}</span>
                    </div>
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <div className="text-[10px] uppercase tracking-[0.16em] font-black text-[var(--gold)] mb-1">
                      Newseze Wire · {(SECTION_LABEL[lead.category] || lead.category || "News").toUpperCase()}
                    </div>
                    <div className="text-[19px] font-black leading-tight tracking-[-0.015em] text-[var(--ink)] group-hover:text-[var(--blue)] line-clamp-3 mb-2">{lead.title}</div>
                    {(lead.narrative || lead.summary || lead.why_it_matters) && (
                      <p className="text-[12.5px] text-[var(--muted)] leading-snug line-clamp-3">{(lead.narrative || lead.summary || lead.why_it_matters || "").slice(0, 200)}</p>
                    )}
                    {lead.source && (
                      <div className="text-[9.5px] text-[var(--muted)] mt-2 uppercase tracking-[0.12em] font-bold opacity-70">Wire: {lead.source}</div>
                    )}
                  </div>
                </Link>
                {/* 4 secondary trusted stories — compact list with prominent trust scores */}
                <div className="flex flex-col gap-3" data-testid="most-trusted-list">
                  {rest.map((a) => (
                    <Link key={a.id} to={`/article/${a.id}`} className="group bg-white rounded-lg border border-[var(--line)] p-3 flex gap-3 hover:border-[var(--gold)] hover:bg-[#fff9ed] transition" data-testid={`most-trusted-item-${a.id}`}>
                      <div className="w-24 h-20 shrink-0 bg-cover bg-center rounded" style={{ backgroundImage: `url(${a.image})` }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="inline-flex items-center gap-1 bg-[#0a1f3d] text-white px-1.5 py-0.5 rounded">
                            <Shield size={9} className="text-[var(--gold)]" />
                            <span className="text-[9px] uppercase tracking-[0.14em] font-black">Trust {a.trust}</span>
                          </span>
                          <span className="text-[9.5px] uppercase tracking-[0.14em] font-black text-[var(--gold)]">Newseze Wire · {SECTION_LABEL[a.category] || a.category}</span>
                        </div>
                        <div className="text-[13.5px] font-black leading-snug text-[var(--ink)] group-hover:text-[var(--blue)] line-clamp-2">{a.title}</div>
                        {a.source && (
                          <div className="text-[9.5px] text-[var(--muted)] mt-1 uppercase tracking-[0.12em] font-bold opacity-70">Wire: {a.source}</div>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          );
        })()}

        {/* ============================= AROUND THE NATION — headlines from competitors / other outlets we don't lead with ============================= */}
        {aroundNation.length > 0 && (
          <section className="py-8" data-testid="section-around-nation">
            <SectionHead color="#0a1f3d" icon={<Globe size={16} />} title="Around the Nation" subtitle="What other outlets are leading with right now · Fox · CNN · Newsmax · Reuters · CBS" href="/other-voices" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="around-nation-grid">
              {aroundNation.map((a) => (
                <a
                  key={a.id}
                  href={a.link}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  referrerPolicy="no-referrer"
                  className="group bg-white rounded-md border border-[var(--line)] p-3 hover:border-[var(--gold)] hover:shadow-md transition flex flex-col gap-2"
                  data-testid={`around-nation-${a.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] uppercase tracking-[0.14em] font-black text-[var(--gold)]">{a.outlet_label}</span>
                    <ExternalLink size={11} className="text-[var(--muted)] opacity-60 group-hover:opacity-100" />
                  </div>
                  <div className="text-[13px] font-black leading-snug text-[var(--ink)] group-hover:text-[var(--blue)] line-clamp-4">{a.title}</div>
                  <div className="mt-auto flex items-center justify-between text-[9.5px] text-[var(--muted)] uppercase tracking-[0.12em] font-bold opacity-70">
                    <span>Wire: {a.source}</span>
                    {typeof a.trust === "number" && a.trust > 0 && (
                      <span className="text-[var(--blue)]">Trust {a.trust}</span>
                    )}
                  </div>
                </a>
              ))}
            </div>
            <div className="mt-3 text-[11px] text-[var(--muted)] italic">
              Headlines link out to the publisher. Newseze does not redistribute their articles — see &ldquo;Other Voices&rdquo; for lane-organized commentary.
            </div>
          </section>
        )}

        {/* ============================= 2. BREAKING NEWS (15 most recent imaged) ============================= */}
        {withPhotos(allRecent.length > 0 ? allRecent : latest).length > 0 && (
          <section className="py-8" data-testid="section-breaking">
            <SectionHead color="#dc2626" icon={<span className="w-2 h-2 rounded-full bg-[#dc2626] animate-pulse" />} title="Breaking News" subtitle="Real-time wire across all six subjects · refreshed hourly" href="/news" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {withPhotos(allRecent.length > 0 ? allRecent : latest).slice(0, 15).map((a) => (
                <NewsCard key={a.id} article={a} onBrief={setBrief} onShare={setShare} />
              ))}
            </div>
          </section>
        )}

        {/* ============================= 3. LOCAL NEWS (multi-source variety) ============================= */}
        {sec("local").length > 0 && (() => {
          // Cap at most 2 stories per source so the local grid feels like
          // local news from the entire country, not just one TV affiliate.
          const seenSrc = {};
          const diverse = [];
          for (const a of sec("local")) {
            const s = a.source || "Local";
            if ((seenSrc[s] || 0) >= 2) continue;
            seenSrc[s] = (seenSrc[s] || 0) + 1;
            diverse.push(a);
            if (diverse.length >= 12) break;
          }
          return (
            <section className="py-8" data-testid="section-local">
              <SectionHead color="#0e7490" icon={<MapPin size={16} />} title="Local News" subtitle="Your city, your state — multi-source: ABC, CBS, NBC, Fox affiliates + local print" href="/section/local" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {diverse.map((a) => (<NewsCard key={a.id} article={a} onBrief={setBrief} onShare={setShare} />))}
              </div>
            </section>
          );
        })()}

        {/* ============================= 4. WORLD (moved up — right after Local) ============================= */}
        {withPhotos(feat?.international_top).length > 0 && (
          <section className="py-8" data-testid="section-world">
            <SectionHead color="#1e40af" icon={<Globe size={16} />} title="World" subtitle="Independent international reporting · no state-sponsored wires" href="/section/world" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {withPhotos(feat.international_top).slice(0, 12).map((a) => (
                <NewsCard key={a.id} article={a} onBrief={setBrief} onShare={setShare} />
              ))}
            </div>
          </section>
        )}

        {/* ============================= 4. POLITICS & GOVERNMENT ============================= */}
        {sec("government").length > 0 && (
          <section className="py-8" data-testid="section-politics">
            <SectionHead color="#1e3a8a" icon={<Landmark size={16} />} title="Politics & Government" subtitle="Federal · state · congress · courts" href="/section/government" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {sec("government").slice(0, 12).map((a) => (<NewsCard key={a.id} article={a} onBrief={setBrief} onShare={setShare} />))}
            </div>
          </section>
        )}

        {/* ============================= 5. CONSUMER & ECONOMY ============================= */}
        {(() => {
          const items = deriveByKW(CONSUMER_KW, 12);
          // Fall back to economy section if keyword filter is sparse
          const final = items.length >= 3 ? items : (items.length ? items : sec("economy").slice(0, 12));
          if (!final.length) return null;
          return (
            <section className="py-8" data-testid="section-consumer">
              <SectionHead color="#10b981" icon={<ShoppingCart size={16} />} title="Consumer & Economy" subtitle="Inflation · housing · jobs · taxes · retirement · healthcare costs" href="/section/money" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {final.slice(0, 12).map((a) => (<NewsCard key={a.id} article={a} onBrief={setBrief} onShare={setShare} />))}
              </div>
            </section>
          );
        })()}

        {/* ============================= 6. CRIME & PUBLIC SAFETY ============================= */}
        {(() => {
          const items = deriveByKW(CRIME_KW, 12);
          if (items.length < 3) return null;
          return (
            <section className="py-8" data-testid="section-crime">
              <SectionHead color="#b91c1c" icon={<Shield size={16} />} title="Crime & Public Safety" subtitle="Crime · law enforcement · border · public safety alerts" href="/section/local" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.slice(0, 12).map((a) => (<NewsCard key={a.id} article={a} onBrief={setBrief} onShare={setShare} />))}
              </div>
            </section>
          );
        })()}

        {/* Banner ad */}
        <AdSlot size="banner" slot="homepage-mid2-banner" />

        {/* ============================= 7. TECHNOLOGY & AI ============================= */}
        {(() => {
          const techSec = sec("technology");
          const aiDerive = deriveByKW(TECH_AI_KW, 12).filter((a) => !techSec.some((t) => t.id === a.id));
          const items = [...techSec, ...aiDerive];
          if (items.length === 0) return null;
          return (
            <section className="py-8" data-testid="section-tech-ai">
              <SectionHead color="#7c3aed" icon={<Cpu size={16} />} title="Technology & AI" subtitle="Artificial intelligence · chips · cyber · platforms" href="/section/technology" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.slice(0, 12).map((a) => (<NewsCard key={a.id} article={a} onBrief={setBrief} onShare={setShare} />))}
              </div>
            </section>
          );
        })()}

        {/* ============================= 8. BUSINESS & MARKETS ============================= */}
        {(sec("economy").length + sec("markets").length) > 0 && (() => {
          const seen = new Set();
          const out = [];
          [...sec("markets"), ...sec("economy")].forEach((a) => {
            if (!seen.has(a.id)) { seen.add(a.id); out.push(a); }
          });
          return (
            <section className="py-8" data-testid="section-business">
              <SectionHead color="#0f766e" icon={<Briefcase size={16} />} title="Business & Markets" subtitle="Wall Street · earnings · corporate · trade" href="/section/markets" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {out.slice(0, 12).map((a) => (<NewsCard key={a.id} article={a} onBrief={setBrief} onShare={setShare} />))}
              </div>
            </section>
          );
        })()}

        {/* ============================= 9. HEALTH & MEDICAL ============================= */}
        {(() => {
          const healthSec = [...sec("health"), ...sec("medicine")];
          const healthDerive = deriveByKW(HEALTH_KW, 12).filter((a) => !healthSec.some((t) => t.id === a.id));
          const items = [...healthSec, ...healthDerive];
          if (items.length === 0) return null;
          const seen = new Set(); const out = [];
          items.forEach((a) => { if (!seen.has(a.id)) { seen.add(a.id); out.push(a); } });
          return (
            <section className="py-8" data-testid="section-health">
              <SectionHead color="#dc2626" icon={<HeartPulse size={16} />} title="Health & Medical" subtitle="Research · public health · medicine · wellness" href="/section/health" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {out.slice(0, 12).map((a) => (<NewsCard key={a.id} article={a} onBrief={setBrief} onShare={setShare} />))}
              </div>
            </section>
          );
        })()}

        {/* ============================= 10. INVESTIGATIONS (Newseze in-house deep-dives) ============================= */}
        <section className="py-10" data-testid="section-investigations">
          <SectionHead color="#0a1f3d" icon={<Headphones size={16} />} title="Investigations" subtitle="Where credibility is built — Newseze deep-dive reporting on power" href="/section/podcasts" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pods.slice(0, 4).map((p) => <PodCard key={p.id} pod={p} />)}
          </div>
        </section>

        {/* ============================= 10c. NEWSSTAND — long-form magazines ============================= */}
        {newsstand.length > 0 && (
          <section className="py-8" data-testid="section-newsstand">
            <SectionHead color="#1e3a8a" icon={<BookOpen size={16} />} title="Newsstand" subtitle="Long-form magazines — Fast Company · Wired · The Atlantic · The Dispatch · City Journal · Smithsonian · Condé Nast · WSJ Opinion" href="/newsstand" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {newsstand.slice(0, 6).map((a) => (
                <MagazineCard key={a.id} article={a} isPro={false} />
              ))}
            </div>
          </section>
        )}
        {voicePods.length > 0 && (
          <section className="py-8" data-testid="section-voice-podcasts">
            <SectionHead color="#7c2d12" icon={<Headphones size={16} />} title="Editorial Voices" subtitle="Curated commentary — NPR · The Daily · Honestly · Megyn Kelly · The Dispatch · Fox News Rundown" href="/section/voices" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {voicePods.map((p) => (
                <a
                  key={p.id}
                  href={p.external_url || "#"}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  referrerPolicy="no-referrer"
                  className="group rounded-md border border-[var(--line)] bg-white hover:border-[var(--blue)] hover:-translate-y-0.5 transition overflow-hidden flex flex-col"
                  data-testid={`voice-pod-${p.id}`}
                >
                  <div className="aspect-[16/9] bg-cover bg-center" style={{ backgroundImage: `url(${p.cover_image})` }} />
                  <div className="p-3.5 flex-1 flex flex-col">
                    <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
                      <span className="text-[9px] uppercase tracking-[0.14em] font-black bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">free</span>
                      <span className="text-[10px] text-[var(--muted)]">{p.duration_min} min</span>
                    </div>
                    <div className="text-[14.5px] font-black leading-snug text-[var(--ink)] group-hover:text-[var(--blue)] line-clamp-2">{p.title}</div>
                    <div className="text-[11.5px] text-[var(--muted)] mt-1 mb-2 line-clamp-2">{p.description}</div>
                    <div className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-[var(--blue)] mt-auto">{p.host} →</div>
                  </div>
                </a>
              ))}
            </div>
          </section>
        )}

        {/* ============================= 11. SPORTS ============================= */}
        {sec("sports").length > 0 && (
          <section className="py-8" data-testid="section-sports">
            <SectionHead color="#ea580c" icon={<Trophy size={16} />} title="Sports" subtitle="Games · scores · trades · championships" href="/section/sports" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {sec("sports").slice(0, 10).map((a) => (<NewsCard key={a.id} article={a} onBrief={setBrief} onShare={setShare} />))}
            </div>
          </section>
        )}

        {/* ============================= 12. LIFESTYLE & TRAVEL ============================= */}
        {(() => {
          const items = deriveByKW(LIFESTYLE_KW, 10);
          if (items.length < 3) return null;
          return (
            <section className="py-8" data-testid="section-lifestyle">
              <SectionHead color="#0891b2" icon={<Plane size={16} />} title="Lifestyle & Travel" subtitle="Travel · food · home · family · wellness" href="/section/culture" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.slice(0, 10).map((a) => (<NewsCard key={a.id} article={a} onBrief={setBrief} onShare={setShare} />))}
              </div>
            </section>
          );
        })()}

        {/* ============================= 13. ENTERTAINMENT (capped at 8 for 70/20/10 mix) ============================= */}
        {(sec("entertainment").length > 0 || sec("hollywood").length > 0) && (
          <section className="py-8" data-testid="section-entertainment">
            <SectionHead color="#a21caf" icon={<Music size={16} />} title="Entertainment" subtitle="Film · TV · music · culture" href="/section/entertainment" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...sec("entertainment"), ...sec("hollywood")].slice(0, 8).map((a) => (
                <NewsCard key={a.id} article={a} onBrief={setBrief} onShare={setShare} />
              ))}
            </div>
          </section>
        )}

        {/* World Wire moved above to position #4 (right after Local) */}

        {/* ===== MEMBER BENEFITS — Six specific value props, one tight strip ===== */}
        <section className="my-10" data-testid="member-benefits">
          <div className="bg-gradient-to-br from-[#0a1f3d] via-[#0e2d63] to-[#1e40af] text-white rounded-2xl px-6 py-7 relative overflow-hidden">
            <div className="absolute inset-0 opacity-15 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 15% 20%, #f5a524 0%, transparent 30%), radial-gradient(circle at 85% 70%, #3b82f6 0%, transparent 35%)" }} />
            <div className="relative">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-5">
                <div>
                  <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-black text-[var(--gold)] mb-1.5">
                    <Gift size={13} /> Specific Member Benefits
                  </div>
                  <h3 className="text-[22px] md:text-[26px] font-black leading-tight tracking-[-0.025em]">
                    Free to read. <span className="text-[var(--gold)]">Premium for $4.99/month.</span>
                  </h3>
                  <p className="text-[12.5px] text-white/75 mt-1.5 leading-snug max-w-[640px]">
                    Built for readers who left mainstream news because of partisanship, opinion drama, and outrage. Newseze keeps the news honest and pays you to stay.
                  </p>
                </div>
                <div className="flex flex-col gap-2 shrink-0 md:w-[200px]">
                  <button onClick={() => setShowBriefSub(true)} className="btn gold w-full justify-center text-[13px]" data-testid="benefits-start">
                    <Sparkles size={13} /> Start free
                  </button>
                  <Link to="/rewards" className="btn w-full justify-center bg-white/15 border-white/20 text-white hover:bg-white/25 text-[12px]" data-testid="benefits-rewards">
                    See all rewards
                  </Link>
                </div>
              </div>

              {/* Six radial benefit tiles — each with a concrete dollar/feature value */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5" data-testid="benefits-radials">
                {[
                  { v: "$25/mo", l: "Earn up to $25/month in credits just for reading + watching", c: "#f5a524", t: "Earn while you read" },
                  { v: "Pro", l: "Subscriber-only investigations & AI summaries", c: "#3b82f6", t: "Exclusive investigations" },
                  { v: "AI", l: "60-sec AI video + audio summary on every major story", c: "#a855f7", t: "AI audio + video" },
                  { v: "%", l: "Discounts at local businesses near you (verified merchants)", c: "#10b981", t: "Local business deals" },
                  { v: "$$$", l: "Reward points redeemable for gift cards or charity", c: "#ec4899", t: "Gift cards & charity" },
                  { v: "+1k", l: "Earn +1,000 credits per friend who joins via your link", c: "#06b6d4", t: "Community referrals" },
                ].map((b) => (
                  <div key={b.t} className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-xl p-3 text-center hover:bg-white/12 transition" data-testid={`benefit-radial-${b.t.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                    <div className="inline-flex items-center justify-center w-11 h-11 rounded-full mb-2 mx-auto" style={{ background: `radial-gradient(circle, ${b.c} 0%, ${b.c}40 100%)` }}>
                      <span className="text-[13px] font-black text-white tabular-nums">{b.v}</span>
                    </div>
                    <div className="text-[11px] font-black uppercase tracking-[0.08em] text-[var(--gold)] mb-1">{b.t}</div>
                    <div className="text-[10.5px] text-white/80 leading-snug">{b.l}</div>
                  </div>
                ))}
              </div>

              {/* Compare tiers — Free vs $4.99 Premium */}
              <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="tier-compare">
                <div className="bg-white/8 rounded-lg p-3.5 border border-white/15">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-black text-white/65 mb-1">Free reader</div>
                  <div className="text-[18px] font-black mb-1.5">$0/month</div>
                  <ul className="text-[11.5px] text-white/85 space-y-1 leading-snug">
                    <li>✓ Unlimited news access</li>
                    <li>✓ Earn rewards as you read</li>
                    <li>✓ 5 AI questions/day</li>
                  </ul>
                </div>
                <div className="bg-[var(--gold)]/15 rounded-lg p-3.5 border border-[var(--gold)]/40">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-black text-[var(--gold)] mb-1">Newseze Premium</div>
                  <div className="text-[18px] font-black mb-1.5 text-[var(--gold)]">$4.99/month</div>
                  <ul className="text-[11.5px] text-white/95 space-y-1 leading-snug">
                    <li>✓ Subscriber-only investigations</li>
                    <li>✓ Unlimited AI summaries + Explain-this-issue</li>
                    <li>✓ 2× rewards · local deals · ad-light reading</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* INTERNATIONAL — Honest News by country */}
        <InternationalSection />

        {/* ============================= INTERESTS — collector & hobbyist subjects ============================= */}
        <section className="py-10 bg-[var(--sky)] -mx-5 px-5 rounded-2xl my-6" data-testid="section-interests">
          <div className="max-w-[1280px] mx-auto">
            <SectionHead color="#7c2d12" icon={<Sparkles size={16} />} title="Your Interests" subtitle="Hobbies, collectors, and passions — Newseze finds the news that fits your interests, not just the day's headlines" href="/#buildeze" />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {Object.entries(INTEREST_KW).map(([key, kw]) => {
                const items = deriveByKW(kw.re, 1);
                const hasLive = items.length > 0;
                return (
                  <button
                    key={key}
                    onClick={() => {
                      if (items[0]) setBrief(items[0]);
                    }}
                    className="bg-white border border-[var(--line)] rounded-lg p-3 text-left hover:border-[var(--gold)] hover:-translate-y-0.5 transition group"
                    data-testid={`interest-${key}`}
                  >
                    <div className="text-[24px] leading-none mb-1">{kw.icon}</div>
                    <div className="text-[13px] font-black text-[var(--ink)] group-hover:text-[var(--blue)] mb-0.5">{kw.label}</div>
                    {hasLive ? (
                      <div className="text-[10.5px] text-[var(--muted)] line-clamp-2 leading-snug">{items[0].title}</div>
                    ) : (
                      <div className="text-[10.5px] text-[var(--muted)] italic">Set up via Buildeze ↓</div>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="mt-4 text-[11.5px] text-[var(--muted)]">
              <strong className="text-[var(--ink)]">Coming soon:</strong> auction results · market values · expert interviews · theft alerts · insurance updates · car shows · authentication tips — for every interest you pick.
            </div>
          </div>
        </section>

        {/* ============================= COMMUNITIES — placeholder hub (Pro launch) ============================= */}
        <section className="py-8" data-testid="section-communities">
          <SectionHead color="#0e1419" icon={<Globe size={16} />} title="Communities" subtitle="Photos · discussions · events · expert opinions — coming with Newseze Pro launch" href="/#buildeze" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { name: "Classic Cars", icon: "🚗", count: "Coming soon", desc: "Photo showcases · auction watching · restoration help · theft alerts" },
              { name: "Watches", icon: "⌚", count: "Coming soon", desc: "Authentication discussions · market trends · new releases · collection showcases" },
              { name: "Real Estate", icon: "🏠", count: "Coming soon", desc: "Market trends in your zip · agent reviews · investment chatter" },
            ].map((c) => (
              <div key={c.name} className="bg-white border border-[var(--line)] rounded-lg p-4 hover:border-[var(--gold)] transition" data-testid={`community-${c.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[26px] leading-none">{c.icon}</span>
                  <div>
                    <h3 className="text-[15px] font-black text-[var(--ink)]">{c.name}</h3>
                    <div className="text-[10px] uppercase tracking-[0.14em] font-black text-[var(--gold)]">{c.count}</div>
                  </div>
                </div>
                <p className="text-[12px] text-[var(--muted)] leading-snug">{c.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Buildeze — personal news profile (FOR YOU feed) */}
        <BuildezeSection onOpenBrief={() => setShowBriefSub(true)} onBrief={setBrief} onShare={setShare} />

        {/* Two-number stat strip — PV (all-time) + PVT (today). No explanation,
            no labels beyond the two acronyms. Per user directive 2026-06-18. */}
        <PvStatsStrip />

        {/* Daily AI-vetted money-making opportunity. Cached once per UTC day. */}
        <MoneyMakerTile />
      </main>

      <Footer onOpenBrief={() => setShowBriefSub(true)} onOpenSponsor={() => setShowSponsor(true)} />

      {brief && <BriefModal article={brief} onClose={() => setBrief(null)} />}
      <PersonalizationModal open={prefsOpen} onClose={() => setPrefsOpen(false)} />
      {share && <ShareModal article={share} onClose={() => setShare(null)} />}
      {showBriefSub && <DailyBriefModal onClose={() => setShowBriefSub(false)} initialReferral={ref} />}
      {showSponsor && <SponsorModal onClose={() => setShowSponsor(false)} />}
    </>
  );
}
